import { Route, Routes, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../auth/use-auth";
import { ThemeProvider } from "../theme/theme-provider";
import { Login } from "../pages/Login";
import { ResetPassword } from "../pages/ResetPassword";
import { Unauthorized } from "../pages/Unauthorized";
import { ProfileNotProvisioned } from "../pages/ProfileNotProvisioned";
import { RecipientFlowComingSoon } from "../pages/RecipientFlowComingSoon";
import { RecipientSignalPage } from "../pages/RecipientSignalPage";
import { AuthStatus } from "../pages/test/AuthStatus";
import { SignalsPage } from "../pages/SignalsPage";
import { SignalDetailModal } from "../features/signals/components/modal/SignalDetailModal";
import { Home } from "../pages/Home";
import { Settings } from "../pages/Settings";
import { Contacts } from "../pages/Contacts";
import { CompanySearch } from "../pages/CompanySearch";
import { IntelToInfluence } from "../pages/IntelToInfluence";
import { Rig } from "../pages/Rig";
import { RfpMgmt } from "../pages/RfpMgmt";
import { Archive } from "../pages/Archive";
import { AttentionObjects } from "../pages/AttentionObjects";
import { Dormant } from "../pages/Dormant";
import { SignalAssigner } from "../pages/SignalAssigner";
import { SweepLog } from "../pages/SweepLog";
import { RadarWorkspace } from "../pages/RadarWorkspace";
import { RadarPublicPage } from "../pages/RadarPublicPage";
import { AppShell } from "../layout/AppShell";
import { RequireAuth } from "./guards/RequireAuth";
import { RequireRole } from "./guards/RequireRole";
import { safeRadarReturnPath } from "../auth/radar-return-path";

function RadarLoginRedirect() {
  const location = useLocation();
  const from = safeRadarReturnPath(`${location.pathname}${location.search}`) ?? "/radar";
  return <Navigate to="/login" replace state={{ from }} />;
}

function PostLoginRedirect() {
  const location = useLocation();
  const state = location.state as { from?: unknown } | null;
  const queryReturn = new URLSearchParams(location.search).get("return_to");
  const destination = safeRadarReturnPath(state?.from) ?? safeRadarReturnPath(queryReturn) ?? "/";
  return <Navigate to={destination} replace />;
}

/**
 * AppRouter is the source of truth for the route TREE. It does NOT own
 * BrowserRouter - App.tsx owns that, mounted once at the application root.
 * Mounting a second BrowserRouter here caused a blank Netlify runtime
 * crash during 7.1 deploy; this file must never import BrowserRouter.
 *
 * 7.1c changes:
 *   - ThemeProvider wraps the entire tree so light/dark theming applies
 *     to every page including the public auth routes.
 *   - The v0.7 functional pages have dedicated shell components
 *     (Contacts, Pursuits, Intel, RfpMgmt, Archive, Dormant, SignalAssigner,
 *     SweepLog) replacing the generic ComingSoon placeholder. (Ghosted was
 *     retired in the Session D contacts build.)
 *
 * Phase C (nav simplification):
 *   - Adds placeholder routes /magic-links and /attention-objects so the
 *     production nav can present the locked target shape. The real surfaces
 *     land in later feature phases.
 *   - Home and Pursuits are hidden from the sidebar in AppShell but keep their
 *     routes HERE, so hiding them is fully reversible (hide, do not delete).
 *     Their pages remain in src/pages.
 *
 * Build placeholders (Company Search + Intel to Influence):
 *   - /company-search and /intel render honest "in build" shells
 *     (src/pages/CompanySearch.tsx, src/pages/IntelToInfluence.tsx). The prior
 *     /intel surface (src/pages/Intel.tsx, a v0.7 index.html port) is preserved
 *     on disk but no longer routed (hide, do not delete).
 */
export function AppRouter() {
  return (
    <ThemeProvider>
      <AuthGate />
    </ThemeProvider>
  );
}

function AuthGate() {
  const { auth } = useAuth();

  const publicRoutes = (
    <>
      {/* Phase D Magic Link recipient view (mock-first, token tier). Rendered
          standalone, outside the app shell and RequireAuth. The RFP recipient
          variant stays on the placeholder until its phase. */}
      <Route path="/s/:token" element={<RecipientSignalPage />} />
      <Route path="/radar/t/:token" element={<RadarPublicPage />} />
      <Route path="/rfp-view" element={<RecipientFlowComingSoon variant="rfp" />} />
    </>
  );

  if (auth.status === "loading") {
    return null;
  }

  if (auth.status === "unauthenticated") {
    return (
      <Routes>
        {publicRoutes}
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ResetPassword mode="request" />} />
        <Route path="/reset-password" element={<ResetPassword mode="confirm" />} />
        <Route path="/test/auth-status" element={<AuthStatus />} />
        <Route path="/radar" element={<RadarLoginRedirect />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  if (auth.status === "unprovisioned") {
    return (
      <Routes>
        {publicRoutes}
        <Route path="/reset-password" element={<ResetPassword mode="confirm" />} />
        <Route path="/test/auth-status" element={<AuthStatus />} />
        <Route path="*" element={<ProfileNotProvisioned />} />
      </Routes>
    );
  }

  // status === 'ready'
  return (
    <Routes>
      {publicRoutes}
      <Route path="/login" element={<PostLoginRedirect />} />
      <Route path="/forgot-password" element={<Navigate to="/" replace />} />
      <Route path="/reset-password" element={<ResetPassword mode="confirm" />} />
      <Route path="/unauthorized" element={<Unauthorized />} />
      <Route path="/test/auth-status" element={<AuthStatus />} />
      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        {/* Phase D Signals feed. Signals is the home surface, so root
            canonicalizes to /signals. /signals/:id is a nested child rendered
            through the SignalsPage <Outlet/> as a modal over the feed; because
            the feed layout instance is shared across /signals and /signals/:id,
            it never unmounts, so scroll is preserved when the modal closes. */}
        <Route path="/" element={<Navigate to="/signals" replace />} />
        <Route path="/signals" element={<SignalsPage />}>
          <Route path=":id" element={<SignalDetailModal />} />
        </Route>

        {/* Home page. Hidden from the Phase C nav (Signals is home) but the
            route is preserved so nothing is deleted. */}
        <Route path="/home" element={<Home />} />

        {/* Functional page shells - v0.7 visual parity, Stream 3D data. */}
        <Route path="/contacts" element={<Contacts />} />
        {/* Build placeholder: new left-nav shell, no feature logic yet. */}
        <Route path="/company-search" element={<CompanySearch />} />
        <Route path="/radar" element={<RadarWorkspace />} />
        {/* Relationship Intelligence Graph (RIG) — flagship surface (Variant B,
            sealed 2026-07-22). Reached from the nav and the Signal modal's
            launch button, which passes ?signal=<id>. */}
        <Route path="/rig" element={<Rig />} />
        {/* /pursuits is a frozen legacy surface (retired stage vocabulary and
            practice naming, placeholder rows, unwired zeros). The route is
            retired to /signals so it is unreachable in production. Pursuits.tsx
            is kept for the later rebuild; its Download Report moved to
            Settings > Admin. */}
        <Route path="/pursuits" element={<Navigate to="/signals" replace />} />
        {/* Build placeholder: /intel re-pointed from the legacy Intel shell
            (src/pages/Intel.tsx, preserved on disk) to an honest "in build"
            surface. */}
        <Route path="/intel" element={<IntelToInfluence />} />
        <Route path="/rfp" element={<RfpMgmt />} />
        <Route path="/archive" element={<Archive />} />

        <Route path="/magic-links" element={<Navigate to="/settings" replace />} />
        {/* Phase C nav target - placeholder page only. Real Attention Object
            surface is built in a later feature phase. */}
        <Route path="/attention-objects" element={<AttentionObjects />} />

        {/* Admin pages - admin or super_admin required. Reached from the
            role-gated Admin section inside Settings (not the primary nav). */}
        <Route
          path="/dormant"
          element={
            <RequireRole required="admin">
              <Dormant />
            </RequireRole>
          }
        />
        <Route
          path="/coverage"
          element={
            <RequireRole required="admin">
              <SignalAssigner />
            </RequireRole>
          }
        />
        <Route
          path="/sweeplog"
          element={
            <RequireRole required="admin">
              <SweepLog />
            </RequireRole>
          }
        />

        <Route path="/settings" element={<Settings />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
